import type { BakeRequest, BakeResponse } from './heatmapWorker';

interface BakeInput {
  /** Packed [u, v, u, v, ...] in measurement-space coordinates. */
  positions: Float32Array;
  /** Packed [r, g, b, r, g, b, ...] in linear 0-1 range. */
  colors: Float32Array;
  bbox: { minX: number; maxX: number; minY: number; maxY: number };
  naturalSpacing: number;
  resolution: number;
}

interface BakeResult {
  data: Uint8Array;
  resX: number;
  resY: number;
  boundsMin: [number, number];
  boundsMax: [number, number];
}

interface BakeHandle {
  promise: Promise<BakeResult>;
  cancel: () => void;
}

/**
 * Singleton manager around the heatmap bake worker. Callers get back a
 * `{ promise, cancel }` pair — cancelling drops the pending callback so a
 * stale response from a superseded request is silently ignored (the worker
 * itself still finishes the current task, but the main thread never sees it).
 */
class HeatmapWorkerManager {
  private worker: Worker | null = null;
  private pending = new Map<string, { resolve: (r: BakeResult) => void; reject: (e: Error) => void }>();

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      this.worker = new Worker(new URL('./heatmapWorker.ts', import.meta.url), {
        type: 'module',
        name: 'heatmap-worker',
      });
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('HeatmapWorkerManager: failed to init worker:', error);
      this.worker = null;
    }
  }

  private handleMessage(event: MessageEvent<BakeResponse>) {
    const msg = event.data;
    const entry = this.pending.get(msg.id);
    if (!entry) return; // cancelled or unknown
    this.pending.delete(msg.id);

    if (msg.type === 'BAKE_DONE') {
      entry.resolve({
        data: msg.data,
        resX: msg.resX,
        resY: msg.resY,
        boundsMin: [msg.boundsMinX, msg.boundsMinY],
        boundsMax: [msg.boundsMaxX, msg.boundsMaxY],
      });
    } else {
      entry.reject(new Error(msg.error));
    }
  }

  private handleError(event: ErrorEvent) {
    console.error('HeatmapWorkerManager: worker error:', event);
    const err = new Error(event.message || 'Heatmap worker crashed');
    this.pending.forEach(({ reject }) => reject(err));
    this.pending.clear();
  }

  isAvailable(): boolean {
    return this.worker !== null;
  }

  bake(input: BakeInput): BakeHandle {
    if (!this.worker) {
      return {
        promise: Promise.reject(new Error('Heatmap worker not available')),
        cancel: () => {},
      };
    }

    const id = `bake_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const promise = new Promise<BakeResult>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
    });

    const req: BakeRequest = {
      type: 'BAKE',
      id,
      positions: input.positions,
      colors: input.colors,
      bboxMinX: input.bbox.minX,
      bboxMaxX: input.bbox.maxX,
      bboxMinY: input.bbox.minY,
      bboxMaxY: input.bbox.maxY,
      naturalSpacing: input.naturalSpacing,
      resolution: input.resolution,
    };

    // Transfer the input buffers (zero-copy) — they're throwaway here.
    this.worker.postMessage(req, [input.positions.buffer, input.colors.buffer]);

    return {
      promise,
      cancel: () => {
        this.pending.delete(id);
      },
    };
  }
}

export const heatmapWorkerManager = new HeatmapWorkerManager();
